import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { LoadingState } from "./components/LoadingState";
import { useDashboard } from "./context/DashboardContext";
import { ClientPage } from "./pages/ClientPage";
import { ClientReportPage } from "./pages/ClientReportPage";
import { MorningCheckPage } from "./pages/MorningCheckPage";
import { SemrushUploadPage } from "./pages/SemrushUploadPage";
import { UsagePage } from "./pages/UsagePage";
import { ExecutiveReportPage } from "./pages/ExecutiveReportPage";
import { CompliancePage } from "./pages/CompliancePage";
import { KnowledgeBasePage } from "./pages/KnowledgeBasePage";
import { ClientObjectiveRoasPage } from "./pages/ClientObjectiveRoasPage";
import { PromptVaultPage } from "./pages/PromptVaultPage";
import { ConversationsPage } from "./pages/ConversationsPage";
import { BudgetPlanPage } from "./pages/BudgetPlanPage";
import { CostosPage } from "./pages/CostosPage";
import { LoginPage } from "./pages/LoginPage";
import { InvitacionPage } from "./pages/InvitacionPage";
import { SandboxPage } from "./pages/SandboxPage";
import { SocialPage } from "./pages/SocialPage";
import { GoldenPage } from "./pages/GoldenPage";
import { AdsTrackingPage } from "./pages/AdsTrackingPage";

function ProtectedRoute({
  allowed,
  children,
}: {
  allowed?: boolean;
  children: ReactNode;
}) {
  const { sessionReady } = useDashboard();
  if (!sessionReady) return <LoadingState />;
  if (!allowed) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function Inicio({ ruta }: { ruta: string | null }) {
  const { sessionReady } = useDashboard();
  if (!sessionReady) return <LoadingState />;
  if (ruta) return <Navigate to={ruta} replace />;
  return <div className="page"><p>Tu cuenta no tiene módulos habilitados. Pide acceso al equipo de Wolf.</p></div>;
}

export default function App() {
  const { session } = useDashboard();
  const isClientScoped = Boolean(session?.isClientUser || session?.isClientPortal);
  // El servidor entrega los módulos visibles de la cuenta; sin lista, no se muestra ninguno.
  const ve = (modulo: string) => Boolean(session?.modulos?.includes(modulo));
  // Primer módulo habilitado de la cuenta, en este orden, cuando no ve Morning Check; es la página de entrada y el destino de las rutas cerradas.
  const rutaDeEntrada = [
    ve("social") ? "/social" : null,
    ve("cliente") ? "/cliente" : null,
    ve("reporte_cuenta") ? "/reporte-camilia" : null,
    ve("conversaciones") ? "/conversaciones" : null,
    ve("gertrudis") && session?.canViewGertrudis ? "/gertrudis" : null,
    ve("notebook") && session?.canViewNotebook ? "/notebook" : null,
    ve("objetivos_roas") && session?.canViewObjectiveRoas ? "/objetivos-roas" : null,
    ve("presupuestos") && session?.canEditBudgetPlan ? "/presupuestos" : null,
    ve("costos") && session?.canViewCosts ? "/costos" : null,
    ve("usage_monitor") && session?.canViewUsage ? "/uso" : null,
    ve("gtm_compliance") ? "/compliance" : null,
    ve("semrush_upload") && session?.canUploadSemrush ? "/semrush" : null,
    ve("golden") && session?.canEditGolden ? "/golden" : null,
  ].find(Boolean) ?? null;

  return (
    <Routes>
      <Route path="login" element={<LoginPage />} />
      <Route path="invitacion" element={<InvitacionPage />} />
      <Route path="reporte-ejecutivo" element={<ExecutiveReportPage />} />
      <Route
        path="sandbox"
        element={
          <ProtectedRoute allowed={ve("sandbox")}>
            <SandboxPage />
          </ProtectedRoute>
        }
      />
      <Route element={<AppShell />}>
        <Route index element={ve("morning_check") && !isClientScoped ? <MorningCheckPage /> : <Inicio ruta={rutaDeEntrada} />} />
        <Route path="cliente" element={<ProtectedRoute allowed={ve("cliente")}><ClientPage /></ProtectedRoute>} />
        <Route path="social" element={<ProtectedRoute allowed={ve("social")}><SocialPage /></ProtectedRoute>} />
        <Route path="reporte-camilia" element={<ProtectedRoute allowed={ve("reporte_cuenta")}><ClientReportPage /></ProtectedRoute>} />
        <Route path="conversaciones" element={<ProtectedRoute allowed={ve("conversaciones")}><ConversationsPage /></ProtectedRoute>} />
        <Route path="gertrudis" element={<ProtectedRoute allowed={ve("gertrudis") && session?.canViewGertrudis}><PromptVaultPage /></ProtectedRoute>} />
        <Route path="vault" element={<Navigate to="/gertrudis" replace />} />
        <Route path="gemini" element={<Navigate to="/gertrudis?tab=quotas" replace />} />
        <Route path="uso" element={<ProtectedRoute allowed={ve("usage_monitor") && session?.canViewUsage}><UsagePage /></ProtectedRoute>} />
        <Route path="semrush" element={<ProtectedRoute allowed={ve("semrush_upload") && session?.canUploadSemrush}><SemrushUploadPage /></ProtectedRoute>} />
        <Route path="compliance" element={<ProtectedRoute allowed={ve("gtm_compliance")}><CompliancePage /></ProtectedRoute>} />
        <Route path="ads-tracking" element={<ProtectedRoute allowed={ve("ads_tracking") && !isClientScoped && Boolean(session?.isAdmin)}><AdsTrackingPage /></ProtectedRoute>} />
        <Route path="notebook" element={<ProtectedRoute allowed={ve("notebook") && session?.canViewNotebook}><KnowledgeBasePage /></ProtectedRoute>} />
        <Route path="objetivos-roas" element={<ProtectedRoute allowed={ve("objetivos_roas") && session?.canViewObjectiveRoas}><ClientObjectiveRoasPage /></ProtectedRoute>} />
        <Route path="presupuestos" element={<ProtectedRoute allowed={ve("presupuestos") && session?.canEditBudgetPlan}><BudgetPlanPage /></ProtectedRoute>} />
        <Route path="golden" element={<ProtectedRoute allowed={ve("golden") && session?.canEditGolden}><GoldenPage /></ProtectedRoute>} />
        <Route path="costos" element={<ProtectedRoute allowed={ve("costos") && session?.canViewCosts}><CostosPage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
